import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Input } from '../containers/Form';
import { closeModal } from '../actions/modal';
import { actions as orgActions } from '../pages/Organizations/redux/actions';
import { notificationActions } from '../components/Notification/redux/actions';
import { GET_ORG_TEMPLATES_ERROR, SAVE_ORG_TEMPLATE_ERROR } from '../pages/Organizations/redux/constants';
import Strings from '../Strings';
import Wizard from '../containers/Modal/Wizard';

export const SAVE_ORG_TEMPLATE_MODAL_NAME = 'save-org-template-modal';
const MAX_TEMPLATE_NAME_LENGTH = 100;
function SaveOrgTemplateModal(props) {
  const { data, onCancel, getOrgTemplates, saveOrgTemplate, showNotification } = props;

  const [templateName, setTemplateName] = React.useState('');
  const [templateList, setTemplateList] = React.useState([]);
  const [confirmDuplicate, setConfirmDuplicate] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState([]);
  const [highlightInvalidFields, setHighlightMissingFields] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    getOrgTemplates().then(resp => {
      if (resp?.type !== GET_ORG_TEMPLATES_ERROR) {
        setTemplateList(resp?.response || []);
      }
    });
  }, []);

  React.useEffect(() => {
    const errors = [];
    if (!templateName.trim()) {
      errors.push({ property: 'name', propertyName: Strings.name, missing: true });
    }
    if (templateName.trim().length > MAX_TEMPLATE_NAME_LENGTH) {
      errors.push({
        property: 'name',
        propertyName: Strings.name,
        errors: [Strings.formatString(Strings.capPatient.wrongFieldLengthFormat, MAX_TEMPLATE_NAME_LENGTH)],
        missing: false }
      );
    }
    if (templateList.some(item => item?.name?.toLowerCase() === templateName.trim().toLowerCase()) && !confirmDuplicate) {
      setConfirmDuplicate(true);
    } else if (!templateList.some(item => item?.name?.toLowerCase() === templateName.trim().toLowerCase()) && confirmDuplicate) {
      setConfirmDuplicate(false);
    }
    setValidationErrors(errors);
  }, [templateName]);

  const onSubmit = () => {
    const organizationId = data?.organizationId ? decodeURIComponent(data.organizationId) : null;

    if (!organizationId) {
      showNotification(Strings.errors.unknownError, 5000, true);
      return;
    }

    setIsSaving(true);

    saveOrgTemplate(organizationId, templateName).then(resp => {
      if (resp?.type === SAVE_ORG_TEMPLATE_ERROR) {
        showNotification(resp?.response?.data?.error?.message || Strings.errors.unknownError, 5000, true);
        setIsSaving(false);
        return;
      }

      showNotification(Strings.templateSaved);
      onCancel();
    });
  };

  const pages = [
    {
      id: 'save-org-template-page',
      title: Strings.saveConfigAsTemplateTitle,
      content: (
        <>
          <Input
            name="templateName"
            id="templateName"
            label={Strings.templateNamePlaceholder}
            type="text"
            value={templateName}
            onChange={e => setTemplateName(e.target.value)}
            maxLength={MAX_TEMPLATE_NAME_LENGTH}
            isRequired
            highlightInvalid={highlightInvalidFields}
            errorsForTooltip={validationErrors.find(v => v.property === 'name')?.errors || []}
          />

          {confirmDuplicate ? (
            <div className="warning-red-text">{Strings.templateNameDuplicateWarning}</div>
          ) : (
            <div />
          )}
        </>
      ),
      nextButton: { text: confirmDuplicate ? Strings.saveTemplateConfirm : Strings.saveTemplate },
      emptyFieldsCount: validationErrors.filter(e => e.missing).length,
      canGoNext: !isSaving && validationErrors.length === 0,
    },
  ];
  
  return (
    <Wizard
      name={SAVE_ORG_TEMPLATE_MODAL_NAME}
      pages={pages}
      onSubmit={onSubmit}
      onNextButtonHover={e => setHighlightMissingFields(e)}
    />
  );
}

SaveOrgTemplateModal.propTypes = {
  data: PropTypes.shape({
    organizationId: PropTypes.any,
  }),
  onCancel: PropTypes.func,
  onOpen: PropTypes.func,
  getOrgTemplates: PropTypes.func,
  saveOrgTemplate: PropTypes.func,
  showNotification: PropTypes.func,
};


const mapDispatchToProps = dispatch => ({
  onCancel: () => dispatch(closeModal(SAVE_ORG_TEMPLATE_MODAL_NAME)),
  getOrgTemplates: () => dispatch(orgActions.getOrgTemplates()),
  saveOrgTemplate: (organizationId, name) => dispatch(orgActions.saveOrgTemplate(organizationId, name)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
});

export default connect(null, mapDispatchToProps)(SaveOrgTemplateModal);
